import React from 'react';
import {
  COLOR_GOLDEN_TAINOI,
  COLOR_KOBI,
  COLOR_MONTE_CARLO,
  COLOR_SAIL,
  COLOR_SALMON,
} from '../../popTheme';
import { Box, Grid, ImageList, Stack, Typography, useTheme } from '@mui/material';
import { selectIconByCategory } from './ResourceIcons';
import { ResourceCard } from './ResourceCard';
import { ResourceCardExpanded } from './ResourceCardExpanded';
import { defineMessages, useIntl } from 'react-intl';

export const CATEGORY = {
  pain: 'PAIN',
  worry: 'WORRY',
  mood: 'MOOD',
  sleep: 'SLEEP',
  substanceUse: 'SUBSTANCE_USE',
  recommended: 'RECOMMENDED',
};

export const getCategoryDisplayLabel = (intl, category, defaultValue = undefined) => {
  const messages = defineMessages({
    resourceCategoryLabelPain: {
      id: 'resource.category.label.pain',
      defaultMessage: 'Pain',
      description: 'Label for the pain category',
    },
    resourceCategoryLabelWorry: {
      id: 'resource.category.label.worry',
      defaultMessage: 'Worry',
      description: 'Label for the worry category',
    },
    resourceCategoryLabelMood: {
      id: 'resource.category.label.mood',
      defaultMessage: 'Mood',
      description: 'Label for the mood category',
    },
    resourceCategoryLabelSleep: {
      id: 'resource.category.label.sleep',
      defaultMessage: 'Sleep',
      description: 'Label for the sleep category',
    },
    resourceCategoryLabelSubstanceUse: {
      id: 'resource.category.label.substanceUse',
      defaultMessage: 'Substances',
      description: 'Label for the substance use category',
    },
    resourceCategoryLabelRecommended: {
      id: 'resource.category.label.recommended',
      defaultMessage: 'Recommended',
      description: 'Label for the recommended category',
    },
  });
  switch (category) {
    case CATEGORY.worry:
      return intl.formatMessage(messages.resourceCategoryLabelWorry);
    case CATEGORY.mood:
      return intl.formatMessage(messages.resourceCategoryLabelMood);
    case CATEGORY.sleep:
      return intl.formatMessage(messages.resourceCategoryLabelSleep);
    case CATEGORY.substanceUse:
      return intl.formatMessage(messages.resourceCategoryLabelSubstanceUse);
    case CATEGORY.recommended:
      return intl.formatMessage(messages.resourceCategoryLabelRecommended);
    case CATEGORY.pain:
      return intl.formatMessage(messages.resourceCategoryLabelPain);
    default:
      return defaultValue;
  }
};

export const getCategoryColor = (category) => {
  switch (category) {
    case CATEGORY.worry:
      return COLOR_SALMON;
    case CATEGORY.mood:
      return COLOR_GOLDEN_TAINOI;
    case CATEGORY.sleep:
      return COLOR_KOBI;
    case CATEGORY.substanceUse:
      return COLOR_MONTE_CARLO;
    default:
      return COLOR_SAIL;
  }
};

export const getCategoryResources = (resources, category, studyCode) => {
  const excludedResourceEnglish = 'iCanCope with Pain App';
  const excludedResourceFrench = 'App iCanCope with Pain';
  if (resources) {
    if (category === CATEGORY.recommended) {
      if (studyCode !== '') {
        return resources.filter((item) => item.isRecommended);
      } else {
        return resources.filter(
          (item) =>
            item.isRecommended &&
            item.title !== excludedResourceEnglish &&
            item.title !== excludedResourceFrench,
        );
      }
    } else {
      if (studyCode !== '') {
        return resources.filter((item) => item.symptom === category);
      } else {
        return resources.filter(
          (item) =>
            item.symptom === category &&
            item.title !== excludedResourceEnglish &&
            item.title !== excludedResourceFrench,
        );
      }
    }
  }
  return [];
};

export const CategorySection = ({ categoryName, categoryData, countLabel }) => {
  const intl = useIntl();
  const theme = useTheme();
  const iconStyle = {
    color: theme.palette.primary.main,
  };

  return (
    <Box key={categoryName}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ ml: '1.75rem', mt: '3rem' }}>
        {selectIconByCategory(categoryName, iconStyle)}
        <Typography variant="h3" sx={{ color: 'text.secondary' }}>
          {getCategoryDisplayLabel(intl, categoryName, '')}
        </Typography>
      </Stack>
      {categoryName !== CATEGORY.recommended && (
        <Typography variant="body2" sx={{ ml: '1.75rem', mt: '1rem', color: 'text.secondary' }}>
          {countLabel}
        </Typography>
      )}
      <Grid
        container
        spacing={2}
        direction="row"
        justify="flex-start"
        alignItems="flex-start"
        sx={{ mt: '2rem', mb: '3rem' }}
      >
        {categoryData.map((cardData) => (
          <Grid item lg={4} xl={3} key={cardData.id}>
            <ResourceCard data={cardData} color={getCategoryColor(cardData.symptom)} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const CategorySectionList = ({ categoryName, categoryData, countLabel }) => {
  const intl = useIntl();
  const theme = useTheme();
  const iconStyle = {
    color: theme.palette.primary.main,
  };

  return (
    <Box key={categoryName}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ ml: '1.75rem', mt: '3rem', mb: '1.25rem' }}
      >
        {selectIconByCategory(categoryName, iconStyle)}
        <Typography variant="h4" sx={{ color: 'text.secondary' }}>
          {getCategoryDisplayLabel(intl, categoryName, '')}
        </Typography>
      </Stack>
      {categoryName !== CATEGORY.recommended && (
        <Typography variant="body1" sx={{ ml: '1.75rem', mb: '2rem', color: 'text.secondary' }}>
          {countLabel}
        </Typography>
      )}
      <ImageList key={categoryName} sx={{ gridAutoFlow: 'column' }}>
        {categoryData.map((cardData) => (
          <ResourceCardExpanded
            key={cardData.id}
            id={cardData.id}
            color={getCategoryColor(cardData.symptom)}
          />
        ))}
      </ImageList>
    </Box>
  );
};
